import type { ReactElement } from "react";
import { useZiehOrdnung } from "@kundeninfo/ui";
import { WahlFeld, feldId } from "./felder.js";
import {
  breitenSumme, mitEinheit, mitWert, spaltenAus, verschoben, wertAn, zahlAus,
} from "../layoutEntwurf.js";
import type { Spalte } from "../layoutEntwurf.js";
import type { PanelProps } from "../LayoutRegister.js";

/**
 * Register „Tabelle": Zeilenlayout und die Positionsspalten (aktiv, Titel, Breite,
 * Ausrichtung, Reihenfolge per Ziehen und ▲/▼), darunter die Breitensumme mit Ampel.
 *
 * Jede Änderung schreibt die VOLLE Liste — im Overlay ersetzt ein Array das der Vorlage
 * ganz (LayoutConfig-Merge); eine halbe Liste hiesse „nur diese Spalten".
 */

const AUSRICHTUNG = [
  { wert: "left", text: "links" }, { wert: "center", text: "mittig" },
  { wert: "right", text: "rechts" },
] as const;

export function TabellePanel(p: PanelProps): ReactElement {
  const katalog = p.stand.katalog;
  const spalten = spaltenAus(p.entwurf, katalog.spalten);
  const summe = breitenSumme(spalten);
  const summeOk = summe >= 99 && summe <= 101;
  const label = (feld: string): string =>
    katalog.spalten.find((k) => k.feld === feld)?.label ?? feld;

  function schreibe(neu: Spalte[], fertig: boolean): void {
    p.onEntwurf(mitWert(p.entwurf, ["positionsspalten"], neu));
    if (fertig) p.onFeldVerlassen();
  }
  function aendere(index: number, teil: Partial<Spalte>, fertig: boolean): void {
    schreibe(spalten.map((s, i) => (i === index ? { ...s, ...teil } : s)), fertig);
  }
  const ordnung = useZiehOrdnung(spalten.length,
    (von, nach) => schreibe(verschoben(spalten, von, nach), true));
  const zeilenlayout = wertAn(p.entwurf, ["zeilenlayout"]);
  const listeFehler = p.feldFehler["positionsspalten"];

  return (
    <div className="lay__panel">
      <WahlFeld id={feldId("zeilenlayout")} label="Zeilenlayout"
        wert={typeof zeilenlayout === "string" ? zeilenlayout : ""}
        optionen={[
          { wert: "", text: `Systemwert (${katalog.zeilenlayout})` },
          { wert: "einzeilig", text: "einzeilig" }, { wert: "zweizeilig", text: "zweizeilig" },
        ]}
        fehler={p.feldFehler["zeilenlayout"]}
        onWahl={(w) => {
          p.onEntwurf(mitWert(p.entwurf, ["zeilenlayout"], w === "" ? undefined : w));
          p.onFeldVerlassen();
        }} />

      <fieldset className="lay__gruppe"
        {...(listeFehler === undefined ? {} : { "aria-describedby": "lay-positionsspalten-fehler" })}>
        <legend>Positionsspalten</legend>
        <ol className="lay__spalten">
          {spalten.map((s, i) => {
            const name = label(s.feld);
            const pfad = `positionsspalten[${i}]`;
            const fehler = (k: string): string | undefined => p.feldFehler[`${pfad}.${k}`];
            const fehlerId = (k: string): string => `${feldId(`${pfad}.${k}`)}-fehler`;
            /** aria-invalid und aria-describedby eines Steuerelements (Fixrunde 1, W2) —
             *  ein Befund am ganzen Eintrag (`feld`) hängt am Schalter „aktiv". */
            const aria = (...ks: string[]): Record<string, string | boolean> => {
              const mit = ks.filter((k) => fehler(k) !== undefined);
              return mit.length === 0 ? { "aria-invalid": false }
                : { "aria-invalid": true, "aria-describedby": mit.map(fehlerId).join(" ") };
            };
            return (
              <li key={s.feld} className="lay__spalte" ref={ordnung.merke(i)} {...ordnung.zeile(i)}>
                <span className="lay__griff" {...ordnung.griff(i)}>⠿</span>
                <input type="checkbox" id={feldId(`${pfad}.aktiv`)} checked={s.aktiv}
                  aria-label={`${name}: aktiv`} {...aria("aktiv", "feld")}
                  onChange={(e) => aendere(i, { aktiv: e.target.checked }, true)} />
                <span className="lay__spaltenname">{name}</span>
                <input type="text" className="lay__eingabe" aria-label={`${name}: Titel`}
                  id={feldId(`${pfad}.titel`)} value={s.titel} {...aria("titel")}
                  onChange={(e) => aendere(i, { titel: e.target.value }, false)}
                  onBlur={p.onFeldVerlassen} />
                <input type="text" inputMode="decimal" className="lay__eingabe lay__eingabe--schmal"
                  aria-label={`${name}: Breite`} id={feldId(`${pfad}.breite`)}
                  value={zahlAus(s.breite, "%")} {...aria("breite")}
                  onChange={(e) => aendere(i, { breite: mitEinheit(e.target.value, "%") ?? "" }, false)}
                  onBlur={p.onFeldVerlassen} />
                <span className="lay__einheit">%</span>
                <select aria-label={`${name}: Ausrichtung`} value={s.align}
                  id={feldId(`${pfad}.align`)} {...aria("align")}
                  onChange={(e) => aendere(i, { align: e.target.value as Spalte["align"] }, true)}>
                  {AUSRICHTUNG.map((a) => <option key={a.wert} value={a.wert}>{a.text}</option>)}
                </select>
                <button type="button" className="lay__pfeil" aria-label={`${name} nach oben`}
                  disabled={i === 0}
                  onClick={() => schreibe(verschoben(spalten, i, i - 1), true)}>▲</button>
                <button type="button" className="lay__pfeil" aria-label={`${name} nach unten`}
                  disabled={i === spalten.length - 1}
                  onClick={() => schreibe(verschoben(spalten, i, i + 1), true)}>▼</button>
                {(["feld", "titel", "breite", "align", "aktiv"] as const).map((k) => {
                  const text = fehler(k);
                  return text === undefined ? null
                    : <span key={k} id={fehlerId(k)} className="lay__feldfehler" role="alert">
                        {text}</span>;
                })}
              </li>
            );
          })}
        </ol>

        <p className={`lay__summe lay__summe--${summeOk ? "ok" : "fehler"}`}>
          Summe der aktiven Breiten: {summe.toLocaleString("de-DE")} %
          {summeOk ? "" : " — erlaubt sind 100 % (± 1)"}
        </p>
        {listeFehler !== undefined && (
          <p id="lay-positionsspalten-fehler" className="lay__feldfehler" role="alert">
            {listeFehler}</p>
        )}
      </fieldset>
      {wertAn(p.entwurf, ["positionsspalten"]) !== undefined && (
        <button type="button" className="lay__knopf"
          onClick={() => {
            p.onEntwurf(mitWert(p.entwurf, ["positionsspalten"], undefined));
            p.onFeldVerlassen();
          }}>Systemspalten</button>
      )}
    </div>
  );
}
